import {
    HubPersona,
    FavoritePerson,
    Person,
    GenericPersona,
    PersonType,
    DistributionListMember,
} from '../..';
import { isValidGuid } from '../generalUtils/guidUtils';
import { isValidEmailAddress } from './emailUtils';

/**
 * Create a hubPersonaId starting from a given personaId
 * @param personaId the personaId to use as a basis for the hubPersonaId
 */

export function makeHubPersonaId(personaId: string): string {
    if (!personaId) {
        throw new Error('Error: makeHubPersonaId called with an empty personaId');
    }

    return 'HubPersonaId_' + personaId;
}

/**
 * Check whether the given HubPersona is of type HubPerson or FavoritePerson
 * @param hubPersona the given HubPersona
 */
export function isHubPerson(hubPersona: HubPersona): hubPersona is Person | FavoritePerson {
    return (
        hubPersona &&
        (hubPersona.type === PersonType.Person || hubPersona.type === PersonType.FavoritePerson)
    );
}

/**
 * Check whether the given HubPersona is generic
 * @param hubPersona the givenHubPersona
 */
export function isHubPersonaGeneric(hubPersona: HubPersona): hubPersona is GenericPersona {
    return hubPersona && hubPersona.type === PersonType.Generic;
}

/**
 * Check whether the given HubPersona is of type HubPerson or PrivateDistributionList
 * @param hubPersona the given HubPersona
 */
export function isHubPersonaMultiselectFriendly(hubPersona: HubPersona): boolean {
    return (
        hubPersona &&
        (hubPersona.type === PersonType.Person ||
            hubPersona.type === PersonType.PrivateDistributionList)
    );
}

export function getLogSafeId(Id: string): string {
    if (isValidGuid(Id)) {
        return 'GUID';
    }

    if (isValidEmailAddress(Id)) {
        return 'email';
    }

    // The first part of an Exchange id is:
    // byte : Encoding type
    // byte : Id type
    // short : Length
    // The 3 first characters of a base64 encoded id will therefore not contain information
    // that can identify the full id, but will give us useful information about the id type.
    return Id ? Id.substring(0, 3) : '';
}

export function makeHubGroupPersonaId(groupId: string, groupSmtp: string): string {
    return groupId ? makeHubPersonaId(groupId) : makeHubPersonaId(groupSmtp.toLowerCase());
}

/**
 * Converts current HubPersona to DistributionListMembers
 * @param persona the given HubPersona
 */
export function makeUniqueDistributionListMembers(persona: HubPersona): DistributionListMember[] {
    const result: DistributionListMember[] = [];
    if (
        isHubPerson(persona) &&
        persona.info.allEmailAddresses &&
        persona.info.allEmailAddresses.length > 0
    ) {
        // It's possible that persona has no linked contacts but has allEmailAddresses, for example favorites.
        // We create a list of emails from allEmailAddresses but try to resolve display name from linkedContacts
        const emailToDisplayNameMap = {};
        if (
            persona.type === PersonType.Person &&
            persona.linkedContacts &&
            persona.linkedContacts.length
        ) {
            persona.linkedContacts.forEach(linkedContact => {
                if (isValidEmailAddress(linkedContact.email)) {
                    emailToDisplayNameMap[linkedContact.email] = linkedContact.displayName;
                }
            });
        }
        persona.info.allEmailAddresses.forEach(emailAddress => {
            if (isValidEmailAddress(emailAddress)) {
                if (!emailToDisplayNameMap[emailAddress]) {
                    emailToDisplayNameMap[emailAddress] = emailAddress;
                }
                return;
            }
        });

        Object.keys(emailToDisplayNameMap).map(email => {
            result.push({
                displayName: emailToDisplayNameMap[email] || email,
                type: PersonType.PrivateDistributionListMember,
                emailAddressWrapper: {
                    Name: emailToDisplayNameMap[email] || email,
                    EmailAddress: email,
                    OriginalDisplayName: email,
                },
                isPdl: false,
                isManuallyTypedEmail: emailToDisplayNameMap[email] ? false : true,
            });
        });
    }
    return result;
}

/**
 * Converts current email to DistributionListMemeber with correct display name
 * @param email the given email
 * @param name the name to be applied to DistributionListMember
 */
export function makeDistributionListMemberForEmailWithName(
    email: string,
    name: string
): DistributionListMember {
    return {
        displayName: name,
        type: PersonType.PrivateDistributionListMember,
        emailAddressWrapper: {
            Name: name,
            EmailAddress: email,
            OriginalDisplayName: email,
        },
        isPdl: false,
        isManuallyTypedEmail: false,
    };
}
